import { JcGeoPoint } from './JcGeoPoint';
import { JcIncrement } from './JcIncrement';
import { JC_SERVER_TIMESTAMP } from './JcServerTimestamp';
import { JcWriteValue } from './JcWriteValue';

/**
 * Per-leaf conversions a backend supplies to {@link JcWriteConverter}: how to
 * represent a `Date`, a {@link JcGeoPoint}, and the {@link JC_SERVER_TIMESTAMP}
 * and {@link JcIncrement} write sentinels as its native value type `V`.
 */
export interface JcWriteLeaves {
    date(value: Date): unknown;
    geoPoint(value: JcGeoPoint): unknown;
    serverTimestamp(): unknown;
    increment(value: JcIncrement): unknown;
}

/**
 * Converts a {@link JcWriteValue} tree into a backend's native representation.
 * The traversal — detecting the three special leaves and recursing arrays and
 * nested DTOs, passing primitives and `null` through — is backend-independent and
 * owned here. A backend supplies only the {@link JcWriteLeaves} for its native
 * value type `V` (e.g. `unknown` for a Firestore SDK); one instance per backend.
 */
export class JcWriteConverter {
    constructor(private readonly leaves: JcWriteLeaves) { }

    /** Converts a write body (a DTO, a partial, or a sentinel-bearing payload) to a record of native values. */
    convert(data: object): Record<string, unknown> {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data)) {
            result[key] = this.convertValue(value);
        }
        return result;
    }

    private convertValue(value: JcWriteValue): unknown {
        if (value === JC_SERVER_TIMESTAMP) return this.leaves.serverTimestamp();
        if (value instanceof Date) return this.leaves.date(value);
        if (value instanceof JcGeoPoint) return this.leaves.geoPoint(value);
        if (value instanceof JcIncrement) return this.leaves.increment(value);
        if (Array.isArray(value)) return value.map((item) => this.convertValue(item));
        if (value !== null && typeof value === 'object') {
            const result: Record<string, unknown> = {};
            for (const [key, item] of Object.entries(value)) {
                result[key] = this.convertValue(item);
            }
            return result;
        }
        return value;
    }
}
